import axios from "axios";
import { createContext, useState } from "react";
import { getAnalytics, logEvent } from "firebase/analytics";
import useFlow from "../hooks/useFlow";
import ConfirmationModal from "../components/ConfirmationModal";
import PurchaseErrorNoAuthModal from "../components/PurchaseErrorNoAuthModal";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { itemCheckoutAmount } from "../utils/storeItem";

export const StoreContext = createContext();

export function StoreProvider({ children }) {
  const { selectedSiteId, selectedLocationId, locationPreferences } = useFlow();

  // Modal states
  const [showPopularItemModal, setShowPopularItemModal] = useState(false);
  const [showFindClientModal, setShowFindClientModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showGiftCardModal, setShowGiftCardModal] = useState(false);
  const [showPlansOfferModal, setShowPlansOfferModal] = useState(false);
  const [showEventDetailModal, setShowEventDetailModal] = useState(false);
  const [showProductListModal, setShowProductListModal] = useState(false);

  // Selected data states
  const [selectedPopularItem, setSelectedPopularItem] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedGiftCard, setSelectedGiftCard] = useState(null);
  const [resultProductList, setResultProductList] = useState([]);
  const [errorTimes, setErrorTimes] = useState(0);

  const [purchaseErrorPhone, setPurchaseErrorPhone] = useState("");
  const [purchaseErrorLoading, setPurchaseErrorLoading] = useState(false);

  const [checkoutPayload, setCheckoutPayload] = useState(null);

  // Saved card states
  const [savedCard, setSavedCard] = useState(null);
  const [hasSavedCard, setHasSavedCard] = useState(false);
  const [loadingCard, setLoadingCard] = useState(false);

  const analytics = getAnalytics();

  console.log("devMode", import.meta.env.DEV);

  const customAxios = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  const purchaseContract = async (data) => {
    const useSaved = data.useSavedCard && savedCard && !savedCard.isExpired;
    let payload = {
      siteId: selectedSiteId,
      locationId: selectedLocationId,
      clientId: selectedClient.id,
      contractId: selectedPopularItem.id,
      source: "checkin",
      ...(useSaved
        ? { storedCardInfo: { lastFour: savedCard.lastFour } }
        : {
            creditCardInfo: {
              creditCardNumber: data.creditCardNumber,
              expMonth: data.expMonth,
              expYear: "20" + data.expYear,
              saveInfo: data.saveInfo ?? true,
            },
          }),
    };

    if (data.promotionCode) payload.promotionCode = data.promotionCode;
    if (data.devMode || import.meta.env.DEV) payload.devMode = true;

    try {
      const resp = await customAxios.post(
        `/products/store/contract/purchase`,
        payload,
      );
      logEvent(analytics, "purchase_contract", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        contractId: selectedPopularItem.id,
        clientId: selectedClient.id,
      });
      return resp;
    } catch (error) {
      logEvent(analytics, "purchase_contract_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        contractId: selectedPopularItem.id,
        clientId: selectedClient.id,
        error,
        isError: true,
      });
      console.error(error);
      // Capture purchase errors in Sentry
      Sentry.captureException(error, {
        tags: {
          context: "purchase_contract",
          siteId: String(selectedSiteId),
          locationId: String(selectedLocationId),
        },
        extra: {
          contractId: selectedPopularItem?.id,
          clientId: selectedClient?.id,
          response: error?.response?.data,
        },
      });
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const purchaseItemService = async (data) => {
    let payload = {
      siteId: selectedSiteId,
      locationId: selectedLocationId,
      clientId:
        selectedClient?.id || locationPreferences?.defaultClientId || null,
      source: "checkin",
      items: [
        {
          item: {
            type:
              selectedPopularItem.type.charAt(0).toUpperCase() +
              selectedPopularItem.type.slice(1),
            metadata: {
              id: selectedPopularItem.id,
            },
          },
          quantity: selectedPopularItem?.quantity || 1,
        },
      ],
      payments: [
        {
          type:
            data.useSavedCard && savedCard && !savedCard.isExpired
              ? "StoredCard"
              : "CreditCard",
          metadata: {
            amount: itemCheckoutAmount(selectedPopularItem),
            ...(data.useSavedCard && savedCard && !savedCard.isExpired
              ? {
                  lastFour: savedCard.lastFour,
                }
              : {
                  creditCardNumber: data.creditCardNumber,
                  expMonth: data.expMonth,
                  expYear: "20" + data.expYear,
                  saveInfo: data.saveInfo || false,
                }),
          },
        },
      ],
    };

    if (data.promotionCode) payload.promotionCode = data.promotionCode;
    if (data.devMode) payload.devMode = data.devMode;

    try {
      const resp = await customAxios.post(
        `/products/store/cart/purchase`,
        payload,
      );
      logEvent(analytics, "purchase_item_service", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        itemId: selectedPopularItem.id,
        clientId: selectedClient?.id,
      });
      return resp;
    } catch (error) {
      logEvent(analytics, "purchase_item_service_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        itemId: selectedPopularItem.id,
        clientId: selectedClient?.id,
        error,
        isError: true,
      });
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const purchaseGiftCard = async (data) => {
    let payload = {
      siteId: selectedSiteId,
      locationId: selectedLocationId,
      clientId: selectedClient?.id || locationPreferences?.defaultClientId,
      source: "checkin",
      giftCardId: selectedGiftCard.id,
      amount: selectedGiftCard.price,
      recipientName: `${data.firstName} ${data.lastName}`,
      recipientEmail: data.email,
      recipientMessage: data.message,
    };

    // Use saved card if useSavedCard is true, otherwise use new card info
    if (data.useSavedCard && savedCard && !savedCard.isExpired) {
      payload.paymentType = "storedCard";
      payload.lastFour = savedCard.lastFour;
    } else {
      payload.paymentType = "creditCard";
      payload.creditCardNumber = data.creditCardNumber;
      payload.expMonth = data.expMonth;
      payload.expYear = "20" + data.expYear;
      payload.saveInfo = data.saveInfo || false;
    }

    if (data.promotionCode) payload.promotionCode = data.promotionCode;

    try {
      const resp = await customAxios.post(
        `/products/store/giftcard/purchase`,
        payload,
      );
      logEvent(analytics, "purchase_gift_card", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        giftCardId: selectedGiftCard.id,
        email: data.email,
      });
      return resp;
    } catch (error) {
      logEvent(analytics, "purchase_gift_card_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        giftCardId: selectedGiftCard.id,
        email: data.email,
        error,
        isError: true,
      });

      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const searchItemById = async (filter) => {
    try {
      const resp = await customAxios.get(`/products/store/items/list`, {
        params: {
          siteId: selectedSiteId,
          locationId: selectedLocationId,
          filter,
        },
      });
      logEvent(analytics, "search_item_by_id", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        itemId: filter,
      });
      return resp;
    } catch (error) {
      logEvent(analytics, "search_item_by_id_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        itemId: filter,
        error,
        isError: true,
      });

      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  const purchaseFreeItem = async (client, plan) => {
    if (plan.type === "contract") {
      const payload = {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        clientId: client.id,
        contractId: plan.id,
        source: "checkin",
        creditCardInfo: {
          creditCardNumber: "",
          expMonth: "",
          expYear: "20",
          saveInfo: false,
        },
      };
      try {
        return await customAxios.post(`/products/store/contract/purchase`, payload);
      } catch (error) {
        if (error.response) throw error.response.data?.error;
        throw new Error("Check your network or try refreshing the page.");
      }
    } else {
      const payload = {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        clientId: client.id,
        source: "checkin",
        items: [
          {
            item: {
              type: plan.type.charAt(0).toUpperCase() + plan.type.slice(1),
              metadata: { id: plan.id },
            },
            quantity: plan.quantity || 1,
          },
        ],
        payments: [
          {
            type: "CreditCard",
            metadata: {
              amount: 0,
              creditCardNumber: "",
              expMonth: "",
              expYear: "20",
              saveInfo: false,
            },
          },
        ],
      };
      try {
        return await customAxios.post(`/products/store/cart/purchase`, payload);
      } catch (error) {
        if (error.response) throw error.response.data?.error;
        throw new Error("Check your network or try refreshing the page.");
      }
    }
  };

  const getPlansForSchedule = async (scheduleId) => {
    try {
      const resp = await customAxios.get(`/products/store/service/list`, {
        params: {
          siteId: selectedSiteId,
          classScheduleId: scheduleId,
        },
      });
      logEvent(analytics, "get_plans_for_schedule", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        scheduleId,
      });
      return resp.data;
    } catch (error) {
      logEvent(analytics, "get_plans_for_schedule_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        scheduleId,
        error,
        isError: true,
      });
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  // Get saved credit card for a client
  const getClientSavedCard = async (clientId) => {
    if (!clientId || !selectedSiteId) {
      setHasSavedCard(false);
      setSavedCard(null);
      return;
    }

    try {
      setLoadingCard(true);
      const resp = await customAxios.get(
        `/products/store/client/${clientId}/card`,
        {
          params: {
            siteId: selectedSiteId,
          },
        },
      );

      setHasSavedCard(resp.data.hasSavedCard);
      setSavedCard(resp.data.card);

      logEvent(analytics, "get_client_saved_card", {
        siteId: selectedSiteId,
        clientId,
        hasSavedCard: resp.data.hasSavedCard,
      });
    } catch (error) {
      // Don't block the flow if there's an error fetching the card
      setHasSavedCard(false);
      setSavedCard(null);
      console.error("Error fetching saved card:", error);
    } finally {
      setLoadingCard(false);
    }
  };

  const createStripePaymentIntent = async (amount) => {
    try {
      const resp = await customAxios.post(
        "/products/store/stripe/create-payment-intent",
        {
          amount,
          siteId: selectedSiteId,
          locationId: selectedLocationId,
          clientId: selectedClient?.id || locationPreferences?.defaultClientId || null,
          email: selectedClient?.email || null,
        }
      );
      return resp.data.clientSecret;
    } catch (error) {
      logEvent(analytics, "stripe_create_intent_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        error,
        isError: true,
      });
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Could not initiate Stripe checkout. Please try again.");
    }
  };

  const createStripeSubscription = async () => {
    const contract = selectedPopularItem;
    const clientId = selectedClient?.id || locationPreferences?.defaultClientId || null;
    try {
      const resp = await customAxios.post(
        "/products/store/stripe/create-subscription",
        {
          siteId: selectedSiteId,
          locationId: selectedLocationId,
          clientId,
          contractId: contract.id,
          contractName: contract.name,
          amount: itemCheckoutAmount(contract),
          serviceId: contract.contractItems?.[0]?.id,
          interval: contract.autopayInterval || "month",
          intervalCount: contract.autopayIntervalCount || 1,
          email: selectedClient?.email || null,
        }
      );
      return resp.data; // { clientSecret, subscriptionId, paymentIntentId, customerId }
    } catch (error) {
      logEvent(analytics, "stripe_create_subscription_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        contractId: contract?.id,
        error,
        isError: true,
      });
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Could not initiate subscription checkout. Please try again.");
    }
  };

  const completeStripeCheckout = async (paymentIntentId, subscriptionId = null) => {
    const isContract = selectedPopularItem?.type === "contract";
    const amount = itemCheckoutAmount(selectedPopularItem);

    const clientId =
      selectedClient?.id || locationPreferences?.defaultClientId || null;

    let payload = {
      paymentIntentId,
      siteId: selectedSiteId,
      locationId: selectedLocationId,
      clientId,
      amount,
      source: "checkin",
    };

    if (isContract) {
      payload.type = "contract";
      payload.contractId = selectedPopularItem.id;
      payload.serviceId = selectedPopularItem.contractItems?.[0]?.id || null;
      payload.subscriptionId = subscriptionId;
    } else {
      payload.type = "cart";
      payload.items = [
        {
          item: {
            type:
              selectedPopularItem.type.charAt(0).toUpperCase() +
              selectedPopularItem.type.slice(1),
            metadata: { id: selectedPopularItem.id },
          },
          quantity: selectedPopularItem?.quantity || 1,
        },
      ];
    }

    try {
      const resp = await customAxios.post(
        "/products/store/stripe/complete-purchase",
        payload
      );
      logEvent(analytics, "stripe_purchase", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        itemId: selectedPopularItem?.id,
        clientId: selectedClient?.id,
      });
      return resp;
    } catch (error) {
      logEvent(analytics, "stripe_purchase_error", {
        siteId: selectedSiteId,
        locationId: selectedLocationId,
        itemId: selectedPopularItem?.id,
        clientId: selectedClient?.id,
        error,
        isError: true,
      });
      Sentry.captureException(error, {
        tags: {
          context: "stripe_complete_purchase",
          siteId: String(selectedSiteId),
          locationId: String(selectedLocationId),
        },
        extra: {
          itemId: selectedPopularItem?.id,
          clientId: selectedClient?.id,
          response: error?.response?.data,
        },
      });
      console.error(error);
      if (error.response) throw error.response.data?.error;
      throw new Error("Check your network or try refreshing the page.");
    }
  };

  // const handleOpenHSChat = () => {
  //   window.HubSpotConversations.widget.open();
  // };

  const handlePurchaseError = async () => {
    setPurchaseErrorLoading(true);
    console.log("purchaseErrorPhone", purchaseErrorPhone);
    console.log("selectedPopularItem", selectedPopularItem);
    console.log("selectedClient", selectedClient);
    console.log("checkoutPayload", checkoutPayload);
    const payload = {
      phone: purchaseErrorPhone,
      popularItem: selectedPopularItem,
      client: selectedClient,
      checkout: checkoutPayload,
    };

    await customAxios.post("/products/store/purchase-error", payload);

    setPurchaseErrorLoading(false);
    setErrorTimes(0);
    setCheckoutPayload(null);
    setPurchaseErrorPhone("");
    setSelectedPopularItem(null);
    setSelectedClient(null);
    setShowCheckoutModal(false);
    toast.success("Thank you, we will contact you soon!");
  };

  return (
    <StoreContext.Provider
      value={{
        showPopularItemModal,
        setShowPopularItemModal,
        selectedPopularItem,
        setSelectedPopularItem,
        showFindClientModal,
        setShowFindClientModal,
        selectedClient,
        setSelectedClient,
        showCheckoutModal,
        setShowCheckoutModal,
        selectedGiftCard,
        setSelectedGiftCard,
        showGiftCardModal,
        setShowGiftCardModal,
        showPlansOfferModal,
        setShowPlansOfferModal,
        showEventDetailModal,
        setShowEventDetailModal,
        showProductListModal,
        setShowProductListModal,
        resultProductList,
        setResultProductList,
        checkoutPayload,
        setCheckoutPayload,
        getPlansForSchedule,
        purchaseFreeItem,
        purchaseContract,
        purchaseItemService,
        purchaseGiftCard,
        searchItemById,
        errorTimes,
        setErrorTimes,
        // Saved card
        savedCard,
        hasSavedCard,
        loadingCard,
        getClientSavedCard,
        // Stripe checkout
        createStripePaymentIntent,
        createStripeSubscription,
        completeStripeCheckout,
      }}
    >
      {children}
      <ConfirmationModal
        show={errorTimes >= 2 && selectedClient}
        handleClose={() => {
          setErrorTimes(0);
        }}
        btnYesText="Contact Support"
        handlerYes={() => {
          handlePurchaseError();
          setErrorTimes(0);
        }}
        btnNoText="Not now"
        handlerNo={() => {
          setErrorTimes(0);
        }}
        title="Oops, sorry about that"
        body="If you are trying to take a class, head on in, the support team will complete the purchase for you later"
      />

      <PurchaseErrorNoAuthModal
        show={errorTimes >= 2 && !selectedClient}
        handleClose={() => {
          setErrorTimes(0);
        }}
        phone={purchaseErrorPhone}
        setPhone={setPurchaseErrorPhone}
        loading={purchaseErrorLoading}
        handlerYes={handlePurchaseError}
      />

    </StoreContext.Provider>
  );
}
